/*
  Bean & Paw card add-to-cart (premium audit #2, emil-design-eng).
  Progressive enhancement for the native `form 'product'` in snippets/bp-product-card:
  without JS the form still posts to /cart/add. With JS it posts in place, shows button
  states (Adding… → Added ✓), and dispatches Horizon's CartAddEvent with the re-rendered
  cart sections, so the cart drawer opens (auto-open setting), the cart count and the
  free-shipping meter update exactly as they do for Horizon's own product form.
*/
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const FORM_SELECTOR = 'form.bp-card__add';
// Horizon declares `const Theme` in snippets/scripts.liquid: a global lexical binding, not a
// property of window, so read the binding itself.
const cartAddUrl = () => (typeof Theme !== 'undefined' ? Theme.routes?.cart_add_url : undefined);
let liveRegion;

function announce(text) {
  if (!liveRegion) {
    liveRegion = document.createElement('p');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('role', 'status');
    document.body.append(liveRegion);
  }
  liveRegion.textContent = '';
  requestAnimationFrame(() => (liveRegion.textContent = text));
}

function setState(button, state) {
  button.dataset.state = state;
  const label = button.querySelector('.bp-card__cta-label');
  const text = button.dataset[`label${state[0].toUpperCase()}${state.slice(1)}`];
  if (label && text) label.textContent = text;
  button.toggleAttribute('aria-busy', state === 'adding');
}

// Flash the drawer line for the variant just added. The drawer re-renders from the event's
// sections, so wait until that re-render settles, then tag the row once.
function highlightLine(variantId) {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  setTimeout(() => {
    const row = document.querySelector(`.cart-drawer__dialog .cart-items__table-row[data-key^="${variantId}:"]`);
    if (!row) return;
    row.classList.add('bp-line-new');
    row.addEventListener('animationend', () => row.classList.remove('bp-line-new'), { once: true });
  }, 320);
}

async function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
  if (!cartAddUrl()) return; // let the native post happen
  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (!button || button.dataset.state === 'adding') return;
  setState(button, 'adding');

  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  try {
    const cfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(cartAddUrl(), {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'text/html' },
    }).then((r) => r.json());

    if (response.status) {
      form.dispatchEvent(new CartErrorEvent(form.id, response.message, response.description, response.errors));
      setState(button, 'error');
      announce(response.message || button.dataset.labelError);
      setTimeout(() => setState(button, 'idle'), 2600);
      return;
    }

    form.dispatchEvent(
      new CartAddEvent({}, String(formData.get('id')), {
        source: 'bp-product-card',
        itemCount: Number(formData.get('quantity')) || 1,
        productId: form.dataset.productId,
        sections: response.sections,
      })
    );
    highlightLine(String(formData.get('id')));
    setState(button, 'added');
    announce(button.dataset.labelAdded);
    setTimeout(() => setState(button, 'idle'), 1600);
  } catch (error) {
    console.error(error);
    form.submit(); // fall back to the native post
  }
}

if (!window.__bpCartAdd) {
  window.__bpCartAdd = true;
  document.addEventListener('submit', onSubmit);
}
